import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { Subscription } from 'rxjs';
import {
    AGURenderService,
    AGUSortOption,
    AGUToastService,
    AGUViewportService,
    AGUAppLocation,
    AGUAppModuleAccent,
    AGUTabStripTab,
    AGUSearchAttachment,
    AGUResultCount,
    FiltersFromEcho,
    FinderPageState,
    FinderTab,
    RevealDirection,
    InitialFinderState,
    IsModuleReentry,
    PageResultCount,
    ResetFilters,
    StripQueryGroups,
    WithPage,
    WithQuery,
    FinderTabScrollMemory,
    OriginOf,
    AGUAdPlacement,
    AGUAdSessionService,
} from 'agu_app_shell';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { RaCompositionState } from '../../ra-composition.state';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { ResearchApiService } from '../../services/research-api.service';
import { MemberItemStateChange, MemberItemStateService } from '../../services/member-item-state.service';
import { ItemKind, JobSort, ResearchJobFacets, ResearchJobFilterInput, ResearchJobMatch, ResearchJobPreferenceInput, ResearchJobSearchResult, ResearchMemberDocument, ResearchQueryInput, UnderstoodJobConstraint } from '../../models/research.models';
import { BuildJobFilterPills, ClearRegionHiddenFilters, CountActiveJobFilterGroups, RegionHiddenGroups, JOB_QUERY_GROUP_FIELDS, JobPillGroupOf, JobRegion, JobWorkLocation, RemoveJobFilterGroup } from './job-filters.component';
import { ActiveFilterPill } from '../../finder/active-filter-pills';
import { ActiveGroupsFrom, PhoneFinderChrome, ToSortOptions } from '../../finder/phone-finder-chrome';
import { DescribeError } from '../shared/describe-error';
import { HeldSkillNames } from '../career-explorer/career-explorer.state';
import { IsoDateOffsetDays } from '../shared/grant-dates';
import { RECOMMENDED_COUNT } from '../shared/recommended-count';
import { AssertRecommendationsRefreshed } from '../shared/recommendations-refreshed';
import { LoadFailures } from '../shared/load-failures';
import { BrowseAllLabel, FinderAllEmptyCopy, FinderRecommendedEmptyCopy, LISTING_GONE_COPY, ListErrorCopy, STATE_NOUN, StatePanelCopy } from '../shared/state-copy';
import { PortalConfigService } from '../../services/portal-config.service';
import { DismissedSlots } from '../shared/dismissed-slots';
import { PinnedFirst } from '../shared/pinned-first';
import { NextResultsPanelId } from '../shared/panel-id';

/** Views this module has. Narrows AGUAppLocation for every internal comparison. */
export type JobFinderView = 'search' | 'detail';

const JOB_KIND: ItemKind = 'job';
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** Verbatim from plans/research-accelerator/mockups/Jobs.txt's "Try" row (also present as `trypill` buttons in the bundled HTML mockup). */
const TRY_CHIPS: readonly string[] = ['Remote sensing postdoc', 'Gulf Coast hydrology faculty', 'Climate modeling positions'];

/** What the Job Finder holds while it is applying a Career Explorer path (spec §G): the role, its title, and how many of its skills the CV evidences. */
export interface CxState {
    readonly roleRef: string;
    readonly title: string;
    readonly heldSkills: number;
}

/** The hero's label outside cx mode, and inside it (PJ:1399 `lab.textContent`). */
const HERO_EYEBROW = 'Search jobs';
const HERO_EYEBROW_CX = 'Search jobs · career path applied';
/** The active pill's key: `OnRemoveFilterGroup('cx')` leaves cx mode. */
export const CX_PILL_KEY = 'cx';

/** Tab order for the reveal direction: All then Recommended. */
const TAB_ORDER: ReadonlyArray<FinderTab> = ['all', 'recommended'];

const SORT_OPTIONS: ReadonlyArray<{ key: JobSort; label: string }> = [
    { key: 'best', label: 'Best match' },
    { key: 'newest', label: 'Newest' },
    { key: 'salaryHighLow', label: 'Salary (high to low)' },
    { key: 'titleAZ', label: 'Title A-Z' },
];

/**
 * "No constraint" shape for every ResearchJobFilterInput field this page round-trips —
 * arrays → [], everything else → undefined. Includes the groups only an understood
 * REQUIREMENT can set (seniority, employmentType, companySize, city, countryNot,
 * postedAfter): `FiltersFromEcho` copies exactly the keys listed here, so a key
 * missing from this shape would be dropped on the way back and lost on the next
 * page (jobs query round 1, finding #1).
 */
const EMPTY_JOB_FILTERS: ResearchJobFilterInput = {
    city: [],
    companySize: [],
    country: [],
    countryNot: [],
    employmentType: [],
    fromQuery: [],
    industry: [],
    postedAfter: undefined,
    recommendedOnly: false,
    salaryMaxUSD: undefined,
    salaryMinUSD: undefined,
    section: [],
    seniority: [],
    state: [],
};

/**
 * `ResetFilters` is generic over `Record<string, unknown>` — `ResearchJobFilterInput`
 * is structurally compatible but, as an `interface` with no index signature,
 * isn't assignable to that constraint without a cast. Same justification as
 * `ResetGrantFilters`/`ResetPeopleFilters` (Tasks 16-17): `ResetFilters` only
 * reads `Object.keys`/`typeof` per value.
 */
function ResetJobFilters(filters: ResearchJobFilterInput): ResearchJobFilterInput {
    return ResetFilters(filters as unknown as Record<string, unknown>) as unknown as ResearchJobFilterInput;
}

/**
 * The origin a job opened from the finder carries (H-13): in cx mode the role the path came from, so the detail's crumb reads
 * `Career paths` and returns to the role; otherwise the finder place on screen. The location keeps one `From` level
 * (`WithOneLevelFrom`), so carrying the finder location in cx mode would drop the role.
 */
export function DetailOriginFor(current: AGUAppLocation, inCx: boolean): AGUAppLocation {
    const origin = current.From;
    if (inCx && origin?.ModuleKey === RA_MODULE_KEYS.CareerMap && origin.View === 'role' && origin.RecordID) return origin;
    return current;
}

/** PJ:1403-1404: "Career path: {role}" and, when the CV evidences any, "{n} skills from your CV". */
export function CxChipLabels(cx: CxState): string[] {
    const chips = [`Career path: ${cx.title}`];
    if (cx.heldSkills > 0) chips.push(`${cx.heldSkills} ${cx.heldSkills === 1 ? 'skill' : 'skills'} from your CV`);
    return chips;
}

/**
 * The phone quick chips' groups: the first four of this page's rail group labels, in rail order, without the `Show` toggles
 * (`chips()`, P/Member_Hub.html:918-920; labels as job-filters.component.html writes them).
 */
export const JOB_PHONE_FILTER_GROUPS: readonly string[] = ['Work location', 'Region', 'Employer sector', 'Science area'];

/** Which rail group (by its label) each active-filter pill key belongs to, for the phone chips' `.on` state (P/Member_Hub.html:939-945). */
export const JOB_PILL_GROUP: Readonly<Record<string, string>> = {
    workLocation: 'Work location',
    region: 'Region',
    industry: 'Employer sector',
    section: 'Science area',
    state: 'State',
    country: 'Country',
    salary: 'Salary range',
};

/**
 * Job Finder — job-posting discovery over the AGU jobs corpus (Pinecone
 * `agu-jobs-*`, served via `ResearchApiService.SearchJobs`/`researchJobPortalSearch`).
 * Unlike Grant/Collaborator Finder, `ResearchJobSearchResult` carries an
 * `available` flag instead of assuming the corpus exists — `available ===
 * false` means the feed isn't loaded/configured, and the page renders the
 * same honest "isn't switched on yet" empty state the pre-Task-18 stub used,
 * rather than a search box whose empty results would read as "nothing
 * matches you" (see the module's git history / job-finder-page's prior
 * doc comment).
 *
 * Root component of the `jobs` module — rendered via `ngComponentOutlet` by
 * `RaSurfaceComponent` with no inputs, so every dependency it needs
 * (`ExplorerRaHostAdapter`, `ResearchApiService`, `MemberItemStateService`)
 * comes through constructor injection from the surface's providers.
 * Structure mirrors `GrantFinderPageComponent`/`PeopleFinderPageComponent`
 * (Tasks 16-17) field-for-field; see those files' doc comments for anything
 * not re-explained here.
 *
 * Product states (PD §5): skeletons while a list loads, the error panel in place of a list that failed, and the empty panels from
 * `state-copy.ts`. Truthfulness edits here: the all-empty panel drops "Save as an alert" (P/proto-states.js:97) and the
 * recommended-empty panel drops "and we will email you the moment one matches" (:98) — the hub has no alerts or match emails.
 */
@Component({
    standalone: false,
    selector: 'ra-job-finder',
    templateUrl: './job-finder-page.component.html',
    styleUrl: './job-finder-page.component.scss',
    // data-accent turns this module's colour into the --accent cascade every
    // shared component below reads (the token layer's ra-module-accent block).
    // ra-fullwidth drops the centred 1200px reading column: EVERY mockup screen
    // is full-bleed inside `.main` (`padding:30px 32px 36px`, no max-width
    // anywhere), the sub-views included — `detail.html:29` sets
    // `.shell { max-width:none }` and caps only the inner `.detail.wide` block
    // at 900px, left-aligned. That inner measure lives on the detail view's own
    // :host (modules/shared/_detail-view.scss), so the host class is
    // unconditional here (review I-2).
    host: {
        '[attr.data-accent]': 'Accent',
        class: 'ra-fullwidth',
    },
})
/** The Job Finder page: search, filter rail, tabs (All, Recommended), paging, saved/pinned/dismissed state and the hand-off to the detail view. */
export class JobFinderPageComponent implements OnInit, OnDestroy {
    /** The results region this page's tab strip drives, unique per instance so two RA tabs never collide on `aria-controls` (review M-9). */
    readonly ResultsPanelId = NextResultsPanelId('jobs');

    readonly TryChips = TRY_CHIPS;

    /** This module's colour, read off its own module definition (never restated here). */
    get Accent(): AGUAppModuleAccent {
        return this.composition.AccentOf(RA_MODULE_KEYS.Jobs);
    }

    readonly SortOptions = SORT_OPTIONS;
    /** The sorts for the phone chips' select (P/Member_Hub.html:926-932). */
    readonly PhoneSortOptions: readonly AGUSortOption[] = ToSortOptions(SORT_OPTIONS);
    /** The rail groups the phone chips open the sheet at. */
    readonly FilterGroups = JOB_PHONE_FILTER_GROUPS;
    /** Phone layout and the filter sheet's open state (P/Member_Hub.html:603-620, 915-951). Assigned in the constructor body. */
    readonly Phone: PhoneFinderChrome;
    /** Bumped when a result set lands or the tab switches; cards rise on it (P/Member_Hub.html:1807-1845). */
    RiseStamp = 0;
    /** Bumped on a tab switch only; the results section slides by RevealDirectionValue (P/grant-finder.html:1219-1232). */
    RevealStamp = 0;
    RevealDirectionValue = 0;

    state: FinderPageState<ResearchJobFilterInput> = InitialFinderState(EMPTY_JOB_FILTERS, 'best', 12);
    view: JobFinderView = 'search';
    recordID: string | null = null;

    items: ResearchJobMatch[] = [];
    /** `null` until the first search resolves. The corpus total, per `ResearchJobSearchResult.total` — drives the toolbar caption (`PagerTotal`, not `total`, drives the numbered pager). */
    total: number | null = null;
    /** True when `total` is an estimate (`ResearchJobSearchResult.totalApproximate`: a listing the server read only part of the way) — the caption and badge read "about N". */
    totalApproximate = false;
    /** `ResearchJobSearchResult.pageableTotal`: the items paging can reach (a listing stops at research.jobs.listingMaxItems). Drives the pager only; the caption and badge keep `total`. */
    pageableTotal: number | null = null;
    /** `ResearchJobSearchResult.queryMode` from the last All-tab search — 'unrecognized' drives `IsUnrecognized`. */
    queryMode: ResearchJobSearchResult['queryMode'] | null = null;

    /** null until the first search resolves; `false` means the jobs corpus isn't loaded/configured (server-decided, never a client-side guess — see class doc comment). */
    available: boolean | null = null;

    recommendedItems: ResearchJobMatch[] = [];
    recommendedMode = 'none';
    private recommendedLoaded = false;
    /** Where each dismissed card sat, so Undo restores it in place (J-3). */
    private readonly dismissedSlots = new DismissedSlots<ResearchJobMatch>((i) => i.jobId);
    /** In flight: SetTab's URL sync comes back through OnLocationChanged -> SetTab before the first load lands (one click sent ResearchRecommendations twice). */
    private recommendedLoading = false;

    loading = false;
    error: string | null = null;
    /** The error panel's foot, retry note and Contact support link (PD §5). */
    readonly failures = new LoadFailures();

    /** The product-state copy for this finder (P/proto-states.js:97-98, 115, 121). */
    readonly AllEmptyCopy: StatePanelCopy = FinderAllEmptyCopy(STATE_NOUN.Jobs);
    readonly RecommendedEmptyCopy: StatePanelCopy = FinderRecommendedEmptyCopy(STATE_NOUN.Jobs);
    readonly ErrorCopy: StatePanelCopy = ListErrorCopy(STATE_NOUN.Jobs);
    readonly GoneCopy: StatePanelCopy = LISTING_GONE_COPY;
    readonly BrowseAll = BrowseAllLabel(STATE_NOUN.Jobs);

    facets: ResearchJobFacets | null = null;

    attachment: AGUSearchAttachment | null = null;
    private documentID: string | null = null;
    /** The member's CV on file, offered as a one-click attachment — see `LoadCV`. */
    cvDocument: ResearchMemberDocument | null = null;

    /** Set while a Career Explorer path is applied (cx mode); null otherwise. */
    cx: CxState | null = null;
    /** The chips the cx try-row shows (PJ:1403-1405), built once on entry so the template reads one array. */
    CxChips: readonly string[] = [];
    /** The attachment cx mode put there, so leaving it takes the CV off again (and a CV the member attached themselves stays). */
    private cxAttachedCv = false;
    /** The role whose entry is in flight (matches loading): the location arriving again meanwhile must not start a second one. */
    private cxEntering: string | null = null;
    /** `roleRef|query` of the last hand-off tried: the identical location arriving again (Back) must not re-run it, whether or not it became cx mode. */
    private cxAttempt: string | null = null;
    /** The member's sort before cx mode set Best match; restored when cx mode ends. */
    private sortBeforeCx: string | null = null;
    /** Bumped when cx mode ends: a hand-off still loading from before it never switches cx mode on afterwards. */
    private cxGeneration = 0;

    region: JobRegion = 'all';
    workLocation: JobWorkLocation = 'all';
    postedThisWeek = false;
    showSavedOnly = false;

    /**
     * The topic text the LAST understood search actually embedded, or null when none
     * is standing. A follow-up re-searches WITHOUT re-extracting, which must mean
     * "reuse what was extracted", not "re-embed the raw sentence" — otherwise two
     * pages of one search rank from different vectors (grants round 1, finding #6).
     * `''` is a REAL value here: a constraint-only query keeps its vector (finding #14).
     */
    private understoodText: string | null = null;
    private constraints: UnderstoodJobConstraint[] = [];
    /** Alternates the last understood search embedded, or `null` when none is standing — re-sent on every follow-up so the ranking cache key stays stable. */
    private understoodAlternates: string[] | null = null;

    /** A follow-up re-sends the echoed topic AND its alternates (stable ranking cache key); a fresh submit re-runs intake on the typed text. */
    private QuerySourceForServer(understand: boolean): ResearchQueryInput {
        const text = understand || this.understoodText === null ? this.state.query : this.understoodText;
        return { text, documentIDs: this.state.documentIDs, alternates: understand ? null : this.understoodAlternates };
    }

    /**
     * A follow-up does not re-run intake, so the preferences the last echo carried
     * go back with it — that is what keeps page 2 ranked like page 1 (spec §5.2).
     * Requirements need no such echo: they live in `state.filters` as hard filters.
     */
    private PreferencesForServer(understand: boolean): ResearchJobPreferenceInput[] | undefined {
        if (understand) return undefined;
        const prefs = this.constraints.filter((c) => c.stance === 'preference').map((c) => ({ kind: c.kind, values: c.values, weight: c.weight }));
        return prefs.length > 0 ? prefs : undefined;
    }

    private locationSub: Subscription | null = null;
    private stateSub: Subscription | null = null;
    /**
     * Whether a search has actually run on the All tab. False on a freshly
     * opened module, which is what `IsPreSearch` renders — the page used to fire
     * an EMPTY-query search on open and render "No jobs match your filters."
     * over the result, which reads as a verdict on a search the member never
     * made (live finding #9). Nothing is requested until they ask for something.
     */
    private searchRan = false;
    /** Per-tab scroll offsets (CSV 2026-10-24, Grant Finder row 1). Assigned in the constructor body: a field initializer would read `this.host` before TS assigns constructor-parameter properties (see `LastDismissed$`). */
    private readonly tabScroll: FinderTabScrollMemory;

    /**
     * The module the surface showed before the latest location — seeded with this page's own key
     * so the emission that arrives on subscribe is not mistaken for a re-entry and does not load
     * the CV a second time on top of `ngOnInit` (same seeding as `SavedPageComponent`).
     */
    private previousModuleKey: string = RA_MODULE_KEYS.Jobs;

    constructor(
        private readonly api: ResearchApiService,
        private readonly itemState: MemberItemStateService,
        private readonly host: ExplorerRaHostAdapter,
        private readonly composition: RaCompositionState,
        private readonly render: AGURenderService,
        @Optional() @Inject(AGUViewportService) viewport: AGUViewportService | null = null,
        /** The phone shell's toast, surface-provided (P/Member_Hub.html:953-958); absent outside a surface. */
        @Optional() @Inject(AGUToastService) private readonly toast: AGUToastService | null = null,
        /** The Featured placements' session state, surface-provided (PD §3); absent outside a surface. */
        @Optional() @Inject(AGUAdSessionService) private readonly ads: AGUAdSessionService | null = null,
        /** The memoised portal config, for the error panel's Contact support address (PD §5); absent outside a surface. */
        @Optional() @Inject(PortalConfigService) private readonly portalConfig: PortalConfigService | null = null
    ) {
        this.Phone = new PhoneFinderChrome(viewport, () => this.render.Notify());
        this.tabScroll = new FinderTabScrollMemory(host);
    }

    ngOnInit(): void {
        this.Phone.Start();
        this.locationSub = this.host.Location$.subscribe((loc) => this.OnLocationChanged(loc));
        this.stateSub = this.itemState.Changes$.subscribe((change) => this.OnItemStateChanged(change));
        void this.LoadFacets();
        void this.failures.ReadSupport(this.portalConfig);
        // The Recommended pill carries its count from first paint, as in the mockup (grants.html:446,
        // P-G4/P-C3/P-J3): load in the background; the tab shows progress/errors only while it is open.
        if (!this.recommendedLoaded && !this.recommendedLoading) void this.LoadRecommended();
        void this.LoadCV();
    }

    ngOnDestroy(): void {
        this.locationSub?.unsubscribe();
        this.stateSub?.unsubscribe();
        this.Phone.Stop();
    }

    /** A getter, not a field initializer — see GrantFinderPageComponent.LastDismissed$'s doc comment for why. */
    get LastDismissed$() {
        return this.itemState.LastDismissed$;
    }

    // ── Location / navigation ───────────────────────────────────────────

    private OnLocationChanged(loc: AGUAppLocation): void {
        // The surface keeps visited modules MOUNTED, so ngOnInit runs once per session. A CV
        // uploaded on My Profile after that never reached the "Use my CV" chip until a reload
        // (E-4); re-entering the module re-checks it, the freshness a remount used to give.
        const reentered = IsModuleReentry(RA_MODULE_KEYS.Jobs, this.previousModuleKey, loc.ModuleKey);
        this.previousModuleKey = loc.ModuleKey;
        if (reentered) void this.LoadCV();

        if (loc.ModuleKey !== RA_MODULE_KEYS.Jobs) return;

        if (loc.View === 'detail') {
            this.view = 'detail';
            this.recordID = loc.RecordID ?? null;
            return;
        }

        this.view = 'search';
        this.recordID = null;

        if (loc.RecordID === 'recommended') {
            this.SetTab('recommended');
        }

        // Spec §G: a path handed off from a Career Explorer role opens the finder in cx mode (it searches the query itself).
        const origin = loc.From;
        const query = loc.Query?.trim();
        if (origin?.ModuleKey === RA_MODULE_KEYS.CareerMap && origin.View === 'role' && origin.RecordID && query) {
            const roleRef = origin.RecordID;
            const attempt = `${roleRef}|${query}`;
            if (this.cx?.roleRef !== roleRef && this.cxEntering !== roleRef && this.cxAttempt !== attempt) {
                this.cxAttempt = attempt;
                void this.EnterCxMode(roleRef, query);
            }
            return;
        }
        this.cxAttempt = null;
        // Any Jobs entry that is not a role hand-off ("Browse all jobs", the sidebar's Jobs) is the plain finder (brief §4, I-4).
        if (this.cx !== null || this.cxEntering !== null) this.EndCxMode(!query);

        // CP §3: a location carrying `q=` opens the finder already searching. The same location arriving twice (a Back onto the identical URL) must not re-run it.
        if (query && query !== this.state.query) this.SubmitFresh(query);
    }

    // ── Cx mode (PJ:1382-1424, 1546-1565; spec §G) ──────────────────────

    /**
     * Applies a Career Explorer path: the member's CV attached, the query typed into the card, sort Best match, one fresh understood
     * search, and the card's label, chips and active pill saying so. A role the matches no longer hold (or matches that cannot load)
     * is not a path to apply: the query is searched as an ordinary one.
     *
     * Deviations (PJ:1405, 1414-1416, 1417-1424): no "Ranked by your fit" chip and no per-card `.jfit` line, because the wire has no
     * per-posting skill data; no `#cxBanner`, which the prototype defines but never shows from this entry.
     */
    private async EnterCxMode(roleRef: string, query: string): Promise<void> {
        this.cxEntering = roleRef;
        const generation = this.cxGeneration;
        let next: CxState | null = null;
        try {
            const matches = await this.api.CareerMatches(false);
            const match = matches.matches.find((m) => m.roleRef === roleRef);
            if (match) next = { roleRef, title: match.title, heldSkills: HeldSkillNames(match).size };
            if (next) await this.AttachCvForCx();
        } catch {
            next = null;
            // A failed entry is not an attempt: the next hand-off for this role and query retries it.
            this.cxAttempt = null;
        } finally {
            if (generation === this.cxGeneration) this.cxEntering = null;
        }
        // A plain Jobs entry arrived while this loaded: the member left the path, so nothing of it applies.
        if (generation !== this.cxGeneration) {
            if (this.cxAttachedCv && !this.cx) this.DetachCxCv();
            return;
        }
        this.cx = next;
        this.CxChips = next ? CxChipLabels(next) : [];
        // Best match only for a path that is applied; a role that is gone is an ordinary search and keeps the member's sort.
        if (next) {
            this.sortBeforeCx ??= this.state.sort;
            this.state = { ...this.state, sort: 'best' };
        }
        this.SubmitFresh(query);
        this.render.Notify();
    }

    /** Puts the member's CV on file in the attachment slot (the `Use my CV` path), reading it first when the profile has not loaded. */
    private async AttachCvForCx(): Promise<void> {
        if (!this.cvDocument) await this.LoadCV();
        if (this.cvDocument && !this.attachment) {
            this.OnUseCV();
            this.cxAttachedCv = true;
        }
    }

    /** The pill's ✕: leaves cx mode, restores the label and the try row, keeps the typed query and runs it again as a plain search. */
    LeaveCxMode(): void {
        if (!this.cx) return;
        this.ClearCx();
        // The location still names the role it came from; replace it with the plain finder so it cannot re-enter cx mode.
        const query = this.state.query;
        this.host.ResetTo({ ModuleKey: RA_MODULE_KEYS.Jobs, View: 'search', Query: query || undefined });
        if (query.trim()) this.SubmitFresh(query);
        this.render.Notify();
    }

    /**
     * Cx mode ends because a plain Jobs location arrived (no navigation: the location is already the plain finder). `plainEntry` is true
     * when the location carries no query of its own: the finder then returns to its opening state (one that does is searched by the caller).
     */
    private EndCxMode(plainEntry: boolean): void {
        const wasOn = this.cx !== null;
        this.ClearCx();
        // A plain entry with no query of its own ("Browse all jobs", the sidebar's Jobs) is the finder as it first opens (H-12,
        // P6:1704): the role title cx mode searched does not carry over. A location that names a query is searched by the caller.
        if (wasOn && plainEntry) this.ReturnToPreSearch();
        this.render.Notify();
    }

    /** Everything cx mode set: the path, its chips and pill (the eyebrow reads `cx`), the CV it attached, and the Best match sort. */
    private ClearCx(): void {
        this.cxGeneration++;
        this.cxEntering = null;
        this.cx = null;
        this.CxChips = [];
        this.DetachCxCv();
        if (this.sortBeforeCx !== null) this.state = { ...this.state, sort: this.sortBeforeCx };
        this.sortBeforeCx = null;
    }

    /** Takes off the CV cx mode attached; a CV the member attached themselves stays. */
    private DetachCxCv(): void {
        if (this.cxAttachedCv) {
            this.attachment = null;
            this.state = { ...this.state, documentIDs: [] };
        }
        this.cxAttachedCv = false;
    }

    /** The hero's label (an override while a path is applied). */
    get HeroEyebrow(): string {
        return this.cx ? HERO_EYEBROW_CX : HERO_EYEBROW;
    }

    /**
     * Leaves the job view for where it was opened from (`From`), else this finder's search view — the crumb's first ancestor
     * (PD §6). The "no job specified" state's way out; the loaded view leaves through its own crumb. Never `host.Back()`: the
     * crumb names a place, and the browser's Back still walks history through the URL-synced adapter.
     */
    LeaveDetail(): void {
        this.host.NavigateTo(OriginOf(this.host.CurrentLocation, 'search'));
    }

    /** The gone-listing panel's "See similar" (P/proto-states.js:121): this finder's All tab. */
    OnSeeSimilar(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Jobs, View: 'search' });
        this.SetTab('all');
    }

    /** The gone-listing panel's "Back to Home" (P/proto-states.js:121). */
    OnBackToHome(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Dashboard, View: 'home' });
    }

    /** "Widen research areas" (P/proto-states.js:97-98): the member's profile, where the research areas show. */
    OnWidenResearchAreas(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Profile, View: 'me' });
    }

    /**
     * The detail view's "Recommended" chip (QA 2026-10-05): only for a job opened from the
     * Recommended tab that is among this member's recommendations — the same rule the cards
     * follow (`[Recommended]="state.tab === 'recommended'"`). Pinning or "new" never sets it.
     */
    get DetailIsRecommended(): boolean {
        return this.state.tab === 'recommended' && this.recommendedItems.some((i) => i.jobId === this.recordID);
    }

    OnOpen(item: ResearchJobMatch): void {
        // The finder place on screen (its tab) is the crumb's origin, or in cx mode the role it came from; the module stays mounted, so its query and filters survive.
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Jobs, View: 'detail', RecordID: item.jobId, From: DetailOriginFor(this.host.CurrentLocation, this.cx !== null) });
    }

    // ── Tabs ─────────────────────────────────────────────────────────────

    get Tabs(): AGUTabStripTab[] {
        return [
            // The search's total, the same number the toolbar caption reads (P-J3; mockup
            // `allCount = jobs.length`, jobs.html:859, parity/jobs.md:184) — `items.length` is
            // only the page in front of the member and read `All jobs 12` beside `97 jobs`.
            { Key: 'all', Label: 'All jobs', Count: this.total || 0, CountApproximate: this.totalApproximate },
            // Finding #13 (Task 15 round 1): `recommendedItems` is empty until
            // `LoadRecommended()` runs on the first switch TO this tab, so binding the
            // badge straight to its length made a member with recommendations waiting
            // read `Recommended 0` — while Home, in the same session, said
            // `See 12 recommended` for the same items. `null` renders NO badge
            // (`AGUTabStripTab.Count`: "Absent or null renders no badge — 0 is a real,
            // renderable count"), which is the honest state before the load; after it,
            // a genuine 0 still shows as 0. Home's own `{N}` is the requested page size
            // (`research.recommend.count`), not a count of this member's rows, so
            // seeding the badge from it would render a fabricated number.
            { Key: 'recommended', Label: 'Recommended', Count: this.recommendedLoaded ? this.recommendedItems.length : null },
        ];
    }

    OnTabChange(key: string): void {
        if (key === 'all' || key === 'recommended') this.SetTab(key);
    }

    SetTab(tab: FinderTab): void {
        if (this.state.tab !== tab) {
            this.tabScroll.Switch(this.state.tab, tab);
            // P/grant-finder.html:1219-1232: the incoming section slides in from the side its tab sits on.
            this.RevealDirectionValue = RevealDirection(TAB_ORDER.indexOf(this.state.tab), TAB_ORDER.indexOf(tab));
            this.RevealStamp++; // the cards are re-created on the switch and rise on init through the reveal's first-8 pass
            this.state = { ...this.state, tab };
        }
        if (tab === 'recommended' && !this.recommendedLoaded) {
            if (this.recommendedLoading) this.loading = true;
            else void this.LoadRecommended();
        }
        this.SyncTabToLocation();
    }

    /**
     * Keeps the address bar's `id=recommended` in step with the tab on screen (D-20/G-15).
     * A tab switch and a fresh query (`WithQuery` lands on All) used to change state only, so
     * the URL kept a stale `id=recommended` and browser Back re-applied the Recommended tab
     * the member had already left. `ResetTo` replaces the entry rather than pushing one: a
     * tab is a view of the same place, not somewhere Back should walk through. Its own
     * `Location$` emission comes back through `OnLocationChanged` with the URL already
     * agreeing with the tab, so the loop ends there.
     */
    private SyncTabToLocation(): void {
        const loc = this.host.CurrentLocation;
        if (!loc || loc.ModuleKey !== RA_MODULE_KEYS.Jobs || loc.View !== 'search') return;
        const onRecommended = this.state.tab === 'recommended';
        if ((loc.RecordID === 'recommended') === onRecommended) return;
        this.host.ResetTo({ ModuleKey: RA_MODULE_KEYS.Jobs, View: 'search', RecordID: onRecommended ? 'recommended' : undefined });
    }

    // ── Search / recommendations ────────────────────────────────────────

    /**
     * Whether a follow-up has anything left to ask the server: a topic, a document, or a
     * standing filter. A constraint-only query ("jobs in Ohio") echoes an empty topic, so
     * removing its last pill used to send `text ''` with no filters and render "0 jobs · No
     * jobs match your filters" with nothing standing (G-3).
     */
    private HasSomethingToSearch(): boolean {
        const source = this.QuerySourceForServer(false);
        return (source.text ?? '').trim().length > 0 || (source.documentIDs?.length ?? 0) > 0 || this.ActiveFilterCount > 0;
    }

    /** Back to the module as it opened (`IsPreSearch`) — no request, no result verdict, an empty search box. */
    private ReturnToPreSearch(): void {
        this.searchRan = false;
        this.items = [];
        this.total = null;
        this.totalApproximate = false;
        this.pageableTotal = null;
        this.queryMode = null;
        this.error = null;
        this.understoodText = null;
        this.understoodAlternates = null;
        this.constraints = [];
        this.state = { ...this.state, query: '', page: 1 };
    }

    async Search(understand = false): Promise<void> {
        if (!understand && !this.HasSomethingToSearch()) {
            this.ReturnToPreSearch();
            return;
        }
        this.searchRan = true;
        this.loading = true;
        this.error = null;
        try {
            const result = await this.api.SearchJobs({
                query: this.QuerySourceForServer(understand),
                count: this.state.pageSize,
                page: this.state.page,
                filters: this.ActiveFiltersForServer(),
                sort: this.state.sort as JobSort,
                understand,
                preferences: this.PreferencesForServer(understand),
            });
            if (result.understood) {
                // The echo is the MERGED filter set the server applied (query groups won
                // their group, manual groups survived) — it becomes the page's own
                // filters so the rail shows them, the badge counts them and every
                // follow-up re-sends them (contract §5; grants D9).
                this.state = { ...this.state, filters: FiltersFromEcho(result.understood.filters, EMPTY_JOB_FILTERS) };
                this.understoodText = result.understood.searchText;
                this.constraints = result.understood.constraints ?? [];
                this.understoodAlternates = result.understood.alternates;
            }
            this.items = result.results;
            this.dismissedSlots.Clear();
            // `??`, not `||`: a null total means the server sent none (use the page's size); 0 is a real total.
            this.total = result.total ?? result.results.length;
            this.totalApproximate = result.totalApproximate;
            // `??`, not `||`: null means no pageableTotal was sent (paging reaches `total`); 0 is a real, empty reach.
            this.pageableTotal = result.pageableTotal ?? this.total;
            this.available = result.available;
            this.queryMode = result.queryMode;
            this.itemState.Prime(
                JOB_KIND,
                this.items.map((i) => ({ key: i.jobId, itemState: i.itemState }))
            );
            this.failures.Reset();
        } catch (e) {
            this.error = DescribeError(e);
            this.failures.Record(e);
        } finally {
            this.loading = false;
            this.RiseStamp++; // a fresh result set rises (P/Member_Hub.html:1830)
        }
    }

    async LoadRecommended(): Promise<void> {
        this.recommendedLoading = true;
        const shown = this.state.tab === 'recommended';
        if (shown) {
            this.loading = true;
            this.error = null;
        }
        try {
            const result = await this.api.Recommendations([JOB_KIND], RECOMMENDED_COUNT);
            AssertRecommendationsRefreshed(result, JOB_KIND); // a kind the server could not refresh is a failed load, not an empty list
            this.recommendedItems = result.jobs;
            this.recommendedMode = result.mode;
            this.recommendedLoaded = true;
            this.itemState.Prime(
                JOB_KIND,
                this.recommendedItems.map((i) => ({ key: i.jobId, itemState: i.itemState }))
            );
            if (this.state.tab === 'recommended') this.failures.Reset();
        } catch (e) {
            // A background load that fails stays unloaded, so opening the tab retries it.
            if (this.state.tab === 'recommended') {
                this.error = DescribeError(e);
                this.failures.Record(e);
            }
        } finally {
            this.recommendedLoading = false;
            if (shown || this.state.tab === 'recommended') {
                this.loading = false;
                this.RiseStamp++; // only when the list on screen is the one that landed; a background prefetch must not replay the All tab's rise
            }
        }
    }

    Retry(): void {
        if (this.state.tab === 'recommended') void this.LoadRecommended();
        else void this.Search();
    }

    private async LoadFacets(): Promise<void> {
        try {
            this.facets = await this.api.JobFacets();
        } catch {
            // Facets are supplementary (dropdown/checkbox option lists) — a
            // failure degrades the filter panel to empty option lists rather
            // than blocking the page.
        }
    }

    /**
     * `researchMyProfile` (LoadMyProfile + LoadCurrentCV) rather than
     * `researchHome`, which additionally runs `ComputeRecommendations` across
     * all three kinds (apps/MJAPI/src/resolvers/ResearchMemberResolver.ts
     * `buildHome`) — strictly heavier for a check that only needs
     * `currentCV`. Never `researchRecordVisit`: that mutates the member's
     * visit ledger, which a background CV check has no business doing.
     */
    private async LoadCV(): Promise<void> {
        try {
            const profile = await this.api.MyProfile();
            this.cvDocument = profile.currentCV;
        } catch {
            // The CV quick-attach is a convenience, not a requirement — a
            // failure here just means the chip never appears.
        }
    }

    /** Attaches the member's CV on file as this search's document, the same way a freshly-uploaded file would be — but without minting a new upload or owning its delete lifecycle (see OnAttachmentRemoved: `documentID` stays null here, so removing this chip never calls DocumentDelete on the member's real CV). */
    OnUseCV(): void {
        if (!this.cvDocument) return;
        this.attachment = { Name: this.cvDocument.fileName, Status: 'ready' };
        this.state = { ...this.state, documentIDs: [this.cvDocument.documentID] };
        this.Applied = false; // the results on screen were found without this document
    }

    /** Strips every default/empty field so the server never sees e.g. `section: []`, and combines the client-only Region/Work location/Posted-this-week toggles into their real wire fields. */
    private ActiveFiltersForServer(): ResearchJobFilterInput {
        const f = this.state.filters;
        const out: ResearchJobFilterInput = {};
        if (f.state && f.state.length > 0) out.state = f.state;
        if (f.city && f.city.length > 0) out.city = f.city;
        if (f.industry && f.industry.length > 0) out.industry = f.industry;
        if (f.section && f.section.length > 0) out.section = f.section;
        if (f.seniority && f.seniority.length > 0) out.seniority = f.seniority;
        if (f.employmentType && f.employmentType.length > 0) out.employmentType = f.employmentType;
        if (f.companySize && f.companySize.length > 0) out.companySize = f.companySize;
        if (f.salaryMinUSD !== undefined && f.salaryMinUSD !== null) out.salaryMinUSD = f.salaryMinUSD;
        if (f.salaryMaxUSD !== undefined && f.salaryMaxUSD !== null) out.salaryMaxUSD = f.salaryMaxUSD;

        // A specific Country pick refines/overrides the broad Region toggle —
        // both ultimately target the same `country`/`countryNot` wire fields. An
        // understood exclusion ("international jobs") lands in `countryNot` directly.
        if (f.country && f.country.length > 0) {
            out.country = f.country;
        } else if (this.region === 'us') {
            out.country = ['us'];
        } else if (this.region === 'international') {
            out.countryNot = ['us'];
        } else if (f.countryNot && f.countryNot.length > 0) {
            out.countryNot = f.countryNot;
        }

        if (this.workLocation === 'remote') out.remote = true;
        else if (this.workLocation === 'onsite') out.remote = false;

        if (this.postedThisWeek) out.postedAfter = IsoDateOffsetDays(-7);
        else if (f.postedAfter !== undefined && f.postedAfter !== null) out.postedAfter = f.postedAfter;

        if (f.recommendedOnly) out.recommendedOnly = true;

        // Names the groups the last understood query set, so the server can tell a
        // query group from a manual one and a fresh submit can strip them (contract §5).
        if (f.fromQuery && f.fromQuery.length > 0) out.fromQuery = f.fromQuery;

        return out;
    }

    // ── Search hero ──────────────────────────────────────────────────────

    /** The hero reads "Results shown" while the query on screen is the one that produced the results (P/job-finder.html:1546-1551). */
    Applied = false;

    OnQueryChange(query: string): void {
        this.Applied = false;
        this.understoodText = null;
        this.understoodAlternates = null;
        this.state = { ...this.state, query };
    }

    /** A fresh submit re-extracts; the PREVIOUS query's groups belong to the previous text, so drop them first (manual groups survive — spec D10, grants D9). */
    private SubmitFresh(query: string): void {
        const stripped = StripQueryGroups(this.state.filters, JOB_QUERY_GROUP_FIELDS, this.state.filters.fromQuery ?? [], EMPTY_JOB_FILTERS);
        this.understoodText = null;
        this.understoodAlternates = null;
        this.constraints = [];
        this.Applied = true;
        this.state = { ...WithQuery(this.state, query), filters: stripped };
        this.tabScroll.Clear(); // a fresh result set has no "place" to return to
        this.SyncTabToLocation();
        void this.Search(true);
    }

    OnSubmitted(query: string): void {
        this.SubmitFresh(query);
    }

    OnSuggestionSelected(suggestion: string): void {
        this.SubmitFresh(suggestion);
    }

    async OnFileSelected(file: File): Promise<void> {
        if (file.size > MAX_ATTACHMENT_BYTES) {
            this.attachment = { Name: file.name, Status: 'failed', Error: 'Files must be under 10 MB' };
            return;
        }
        this.attachment = { Name: file.name, Status: 'uploading' };
        try {
            const doc = await this.api.UploadDocument(file, 'search', false);
            this.documentID = doc.documentID;
            this.attachment = { Name: doc.fileName, Status: 'ready' };
            this.state = { ...this.state, documentIDs: [doc.documentID] };
            this.Applied = false; // the results on screen were found without this document
        } catch (e) {
            this.attachment = { Name: file.name, Status: 'failed', Error: e instanceof Error ? e.message : 'Upload failed' };
        }
    }

    async OnAttachmentRemoved(): Promise<void> {
        const id = this.documentID;
        this.attachment = null;
        this.documentID = null;
        this.state = { ...this.state, documentIDs: [] };
        this.Applied = false; // the results on screen were found with the removed document
        if (id) {
            try {
                await this.api.DocumentDelete(id);
            } catch {
                // Best-effort cleanup — the UI has already dropped the attachment
                // either way, so a failed server-side delete isn't worth surfacing.
            }
        }
    }

    // ── Filters / sort / paging ──────────────────────────────────────────

    get ActiveFilterCount(): number {
        return CountActiveJobFilterGroups(this.state.filters, this.region, this.workLocation, this.postedThisWeek);
    }

    OnFiltersChange(filters: ResearchJobFilterInput): void {
        this.state = { ...this.state, filters, page: 1 };
        void this.Search();
    }

    /**
     * Understood pills first (requirements plain, preferences `· preferred`), then
     * the manual pills for every group the query did not claim — a manual Sector
     * pick must not vanish because the query set a State (spec D9).
     *
     * None on the Recommended tab: recommendations come from the member's profile
     * and ignore the rail (the rail itself is not rendered there), so a standing
     * `Location: Ohio` over that list would read as a filter that is not applied.
     * The filters survive underneath and return with the All tab.
     */
    /** The rail groups with at least one active filter — the phone chips render those `.on` (P/Member_Hub.html:653, 939-945). */
    get ActiveFilterGroups(): readonly string[] {
        return ActiveGroupsFrom(this.FilterPills, JOB_PILL_GROUP);
    }

    get FilterPills(): ActiveFilterPill[] {
        if (this.state.tab !== 'all') return [];
        const cxPill = this.cx ? [{ Key: CX_PILL_KEY, Label: 'Career path', Value: this.cx.title }] : [];
        const understood = BuildJobFilterPills(this.state.filters, this.constraints);
        const claimed = new Set(understood.map((p) => p.Key));
        const manual = BuildJobFilterPills(this.state.filters, this.region, this.workLocation, this.postedThisWeek).filter((p) => !claimed.has(p.Key));
        return [...cxPill, ...understood, ...manual];
    }

    OnRemoveFilterGroup(key: string): void {
        if (key === CX_PILL_KEY) { this.LeaveCxMode(); return; }
        this.constraints = this.constraints.filter((c) => JobPillGroupOf(c.kind) !== key);
        if (key === 'workLocation') { this.OnWorkLocationChange('all'); return; }
        if (key === 'region') { this.OnRegionChange('all'); return; }
        if (key === 'postedThisWeek') { this.OnPostedThisWeekChange(false); return; }
        this.state = { ...this.state, filters: RemoveJobFilterGroup(this.state.filters, key), page: 1 };
        void this.Search();
    }

    OnRegionChange(region: JobRegion): void {
        this.region = region;
        // State shows only for the U.S. and Country only for International (job-filters.component.html unmounts the other):
        // a value the new Region hides is cleared with its understood constraint, so no hidden filter stays applied or counted (L79).
        const hidden: readonly string[] = RegionHiddenGroups(region);
        this.constraints = this.constraints.filter((c) => !hidden.includes(JobPillGroupOf(c.kind)));
        this.state = { ...this.state, filters: ClearRegionHiddenFilters(this.state.filters, region), page: 1 };
        void this.Search();
    }

    OnWorkLocationChange(workLocation: JobWorkLocation): void {
        this.workLocation = workLocation;
        this.state = { ...this.state, page: 1 };
        void this.Search();
    }

    OnPostedThisWeekChange(value: boolean): void {
        this.postedThisWeek = value;
        this.state = { ...this.state, page: 1 };
        void this.Search();
    }

    /** The rail's "Recommended only" switch mirrors `filters.recommendedOnly` — a server-side filter on All jobs (spec §7), not a tab switch. */
    OnRecommendedOnlyChange(on: boolean): void {
        this.state = { ...this.state, filters: { ...this.state.filters, recommendedOnly: on }, page: 1 };
        void this.Search();
    }

    OnClearFilters(): void {
        // Clears understood pills as well as manual ones (contract §5); the topic stays.
        this.constraints = [];
        this.state = { ...this.state, filters: ResetJobFilters(this.state.filters), page: 1 };
        this.region = 'all';
        this.workLocation = 'all';
        this.postedThisWeek = false;
        this.showSavedOnly = false;
        void this.Search();
    }

    OnSortChange(sort: string): void {
        this.state = { ...this.state, sort, page: 1 };
        void this.Search();
    }

    OnPageChange(page: number): void {
        this.state = WithPage(this.state, page);
        void this.Search();
    }

    // ── Display lists / empty states ────────────────────────────────────

    /**
     * Scoped to the Recommended tab only — on the All tab this must be empty
     * even if a Recommended item happens to be pinned, otherwise `IsEmpty`
     * below stays false when the All tab's own filtered results are empty
     * (same review finding as Grant/Collaborator Finder, 2026-09-03).
     */
    get PinnedItems(): ResearchJobMatch[] {
        if (this.state.tab !== 'recommended') return [];
        return this.recommendedItems.filter((i) => i.itemState.pinned);
    }

    private get UnpinnedRecommended(): ResearchJobMatch[] {
        return this.recommendedItems.filter((i) => !i.itemState.pinned);
    }

    /**
     * The All tab hoists pinned results to the front of the page it is showing
     * (Ruling R-F12, `PinnedFirst`) — the Recommended tab lifts them into its
     * own "Pinned by you" section instead, so it must not reorder as well.
     */
    get DisplayItems(): ResearchJobMatch[] {
        const source = this.state.tab === 'recommended' ? this.UnpinnedRecommended : PinnedFirst(this.items);
        return source.filter((i) => !this.showSavedOnly || i.itemState.saved);
    }

    /**
     * The All tab before the member has asked for anything. Distinct from
     * "no results": nothing has been searched, so there is nothing to report an
     * absence of, and offering "Clear all filters" for filters they never set
     * only confuses (live finding #9).
     */
    /**
     * The landing strip under the search hero (P/Member_Hub.html:1383-1401): shown while the finder is in its landing state —
     * Recommended, or All before any search — and gone once results show, as the prototype hides it while `#searchSec` is visible.
     */
    get HeroStrip(): AGUAdPlacement | null {
        const landing = this.state.tab === 'recommended' || this.IsPreSearch;
        return landing ? (this.ads?.First('jobs', 'strip') ?? null) : null;
    }

    /** The employer spotlight appended to the filter rail (P/Member_Hub.html:1534-1544). */
    get RailSpot(): AGUAdPlacement | null {
        return this.ads?.First('jobs', 'rail-spot') ?? null;
    }

    /** The featured job, third in the list after the second organic item — only when there are at least two (P/Member_Hub.html:1570-1578). */
    get InListJob(): AGUAdPlacement | null {
        return this.DisplayItems.length >= 2 ? (this.ads?.First('jobs', 'in-list-job') ?? null) : null;
    }

    get IsPreSearch(): boolean {
        return this.state.tab === 'all' && !this.searchRan;
    }

    /**
     * The toolbar's count line (grants.html:722,760) — All tab only, and only
     * once a search has actually run. `ResearchJobSearchResult.total` now
     * carries the served set (Task 16), with `totalApproximate` marking a
     * listing the server read only part of the way — `FormatCountLine`
     * renders that as "about N" (`FormatApproximateCount`). Routed through `PageResultCount` rather than hand-zeroing
     * `Start` on an empty page: a zeroed `Start` produces `{Start:0,End:0}`,
     * which `FormatCountLine` reads as a valid one-item range ("Showing 0 to
     * 0") instead of "0 jobs" (review finding, round 1).
     *
     * The on-page count is `DisplayItems.length`, what the column actually
     * renders, NOT the raw size of the page the server returned: the `Saved
     * only` switch filters the page client-side, so the raw page count
     * printed `Showing 1 to 12` over an empty column (final review, finding
     * #1).
     */
    get ResultCount(): AGUResultCount | null {
        // A failed load shows no count (P/proto-states.js:117, `#count` '').
        if (this.state.tab !== 'all' || this.IsPreSearch || this.error) return null;
        return PageResultCount(this.total, this.state.page, this.state.pageSize, this.DisplayItems.length, 'job', this.totalApproximate);
    }

    /**
     * The pager's total: the reachable items, never more than the server will page (`pageableTotal`), so the last
     * page offered is the last one the server serves; the count line still reads the real `total`.
     */
    get PagerTotal(): number | null {
        // `??`, not `||`: null means "the server sent no pageableTotal" (fall back to `total`); 0 is a real total
        // (an empty listing) and must stay 0.
        return this.pageableTotal ?? this.total;
    }

    get ShowNothingToMatchYet(): boolean {
        return this.state.tab === 'recommended' && this.recommendedMode === 'none' && this.recommendedItems.length === 0;
    }

    /** Spec 2026-09-30 F1: the intake judged the typed text unsearchable. */
    get IsUnrecognized(): boolean {
        return this.state.tab === 'all' && this.queryMode === 'unrecognized';
    }

    get IsEmpty(): boolean {
        return this.DisplayItems.length === 0 && this.PinnedItems.length === 0;
    }

    // ── Card actions ─────────────────────────────────────────────────────

    async OnSave(item: ResearchJobMatch, saved: boolean): Promise<void> {
        await this.itemState.Toggle(JOB_KIND, item.jobId, { saved });
        this.toast?.ShowSaved(saved); // The bookmark toast (P/Member_Hub.html:953-966), once the save has stuck: a failed Toggle throws.
    }

    async OnPin(item: ResearchJobMatch, pinned: boolean): Promise<void> {
        await this.itemState.Toggle(JOB_KIND, item.jobId, { pinned });
    }

    async OnDismiss(item: ResearchJobMatch): Promise<void> {
        await this.itemState.Dismiss(JOB_KIND, item.jobId, item.title);
        const left = this.dismissedSlots.Remove(item, this.items, this.recommendedItems);
        this.items = left.Items;
        this.recommendedItems = left.Recommended;
    }

    async Undo(): Promise<void> {
        await this.itemState.Undo();
    }

    private OnItemStateChanged(change: MemberItemStateChange): void {
        if (change.kind !== JOB_KIND) return;
        const patch = (list: ResearchJobMatch[]): void => {
            const item = list.find((i) => i.jobId === change.key);
            if (item) item.itemState = change.itemState;
        };
        patch(this.items);
        patch(this.recommendedItems);
        // Pinning repartitions the All tab (`DisplayItems` → `PinnedFirst`), and
        // the server-truth emission arrives after an await, outside the click
        // that started it — in a zoneless host nothing else would repaint it.
        this.render.Notify();

        // `confirmed` only, deliberately: every toggle emits twice (optimistic,
        // then server truth), and reloading on both fired two identical searches
        // for one Undo (live finding #18) — the first of which raced the write it
        // was reacting to.
        if (change.confirmed && !change.itemState.dismissed) {
            // A dismissed card is spliced out of `items`/`recommendedItems`, so Undo has nothing left to
            // patch above. Put it back in the slot it left (J-3: a reload re-ranks, and the restored item
            // can land off the page); reload only for an item this page never removed itself.
            const restored = this.dismissedSlots.Restore(change.key, this.items, this.recommendedItems, (i) => ({ ...i, itemState: change.itemState }));
            if (restored) {
                this.items = restored.Items;
                this.recommendedItems = restored.Recommended;
                this.render.Notify();
                return;
            }
            const wasTracked = this.items.some((i) => i.jobId === change.key) || this.recommendedItems.some((i) => i.jobId === change.key);
            // Only an undone dismissal can bring back an item this page does not hold; a save or pin elsewhere (a Saved-board Undo)
            // never re-runs this module's search (F-11).
            if (!wasTracked && change.undismissed && this.view === 'search') {
                if (this.state.tab === 'recommended') void this.LoadRecommended();
                else void this.Search();
            }
        }
    }
}
